import { z } from 'zod/v4';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';

import {
    BaseVisualizationArgsSchema,
    ColumnReferenceSchema,
    createBaseGroupSchema,
    createColumnTransform,
    createGroupedVisualizationSchema,
    createRowTransform,
    createVisualizationResponseSchema,
    ExecutionMetadata,
    FormatFieldSchema,
    LegendConfigSchema,
    RowIndexFieldSchema,
    VisualizationParametersSchema,
} from '../validation/schemas';

const PiePartitionSchema = z.object({
    label: z.string(),
    format: FormatFieldSchema,
    column: ColumnReferenceSchema, // Will be transformed
    rowIndex: RowIndexFieldSchema, // Will be transformed
    color: z.string(),
});

const PieGroupSchema = createBaseGroupSchema(
    z.object({
        partitions: z.array(PiePartitionSchema).default([]),
    }),
);

export const PieOptionsSchema = createGroupedVisualizationSchema(PieGroupSchema, {
    legend: LegendConfigSchema.optional(),
    showPartitionLabels: z.boolean().default(true),
    isDonut: z.boolean().default(true),
});

export const createPieArgsSchema = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    const columnTransform = createColumnTransform(meta);
    const rowIndexTransform = createRowTransform(meta);

    return BaseVisualizationArgsSchema.extend({
        type: z.literal(StudioVisualizationType.Pie),
        config: z.object({
            id: z.string().optional(),
            parameters: VisualizationParametersSchema.optional(),
            options: createGroupedVisualizationSchema(
                PieGroupSchema.extend({
                    partitions: z
                        .array(PiePartitionSchema.extend({ rowIndex: rowIndexTransform, column: columnTransform }))
                        .default([]),
                }),
                {
                    legend: LegendConfigSchema.optional(),
                    showPartitionLabels: z.boolean().default(true),
                    isDonut: z.boolean().default(true),
                },
            ),
        }),
    });
};

export const PieResponseSchema = createVisualizationResponseSchema(StudioVisualizationType.Pie).extend({
    options: PieOptionsSchema,
    data: z.any(),
});

export type PieOptions = z.infer<typeof PieOptionsSchema>;
export type PieResponse = z.infer<typeof PieResponseSchema>;

// Export the generic type for the args schema
export type PieArgsSchema<TColumns extends string = string> = ReturnType<typeof createPieArgsSchema<TColumns>>;
export type PieArgs<TColumns extends string = string> = z.infer<PieArgsSchema<TColumns>>;
